import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { List, X, Check, Plus, Trash2, GripHorizontal } from "lucide-react";

export function InlineDropdownPicker({
  isOpen,
  position,
  context,
  options = [],
  onClose,
  onSelect,
  onClear,
  onAddOption,
  onDeleteOption
}) {
  const [customValue, setCustomValue] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const modalRef = useRef(null);
  const MODAL_HEIGHT = 350; // Estimated max height of the modal
  const MODAL_WIDTH = 320; // Width of the modal (w-80 = 20rem = 320px)

  // Update position when position prop changes - with smart positioning
  useEffect(() => {
    if (position && isOpen) {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      let newTop = position.top;
      let newLeft = position.left;
      
      // Check if modal would overflow at the bottom
      if (position.top + MODAL_HEIGHT > viewportHeight) {
        // Position above the cell instead (subtract modal height and cell height)
        const cellHeight = position.cellHeight || 40;
        newTop = Math.max(10, position.top - MODAL_HEIGHT - cellHeight + 40);
      }
      
      // Check if modal would overflow on the right
      if (position.left + MODAL_WIDTH > viewportWidth) {
        newLeft = Math.max(10, viewportWidth - MODAL_WIDTH - 10);
      }
      
      // Ensure modal doesn't go above viewport
      if (newTop < 10) {
        newTop = 10;
      }
      
      setModalPosition({ top: newTop, left: newLeft });
    }
  }, [position?.top, position?.left, isOpen]);

  // Handle drag events
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setModalPosition({
          top: e.clientY - dragOffset.y,
          left: e.clientX - dragOffset.x
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setCustomValue("");
      setIsDragging(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelect = (option) => {
    if (context.orderId && context.columnName) {
      onSelect(context.orderId, context.columnName, option);
    }
    onClose();
  };

  const handleClear = () => {
    if (context.orderId && context.columnName) {
      onClear(context.orderId, context.columnName);
    }
    // Don't call onClose() here - the clear handler already closes the picker after the API call succeeds
  };

  const handleAddCustom = () => {
    if (customValue.trim() && context.orderId && context.columnName) {
      onAddOption(context.orderId, context.columnName, customValue.trim());
      setCustomValue("");
    }
  };

  const handleDeleteOption = (option, e) => {
    e.stopPropagation();
    if (onDeleteOption && context.columnName) {
      onDeleteOption(context.columnName, option);
    }
  };

  const handleClose = () => {
    setCustomValue("");
    onClose();
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.drag-handle')) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault();
    }
  };

  return (
    <div 
      ref={modalRef}
      className="dropdown-picker-popup fixed bg-white rounded-lg shadow-xl border border-zinc-200 text-zinc-900 w-80"
      style={{ 
        top: `${modalPosition.top}px`, 
        left: `${modalPosition.left}px`,
        zIndex: 9999,
        cursor: isDragging ? 'grabbing' : 'auto'
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={handleMouseDown}
    >
      {/* Header with drag handle */}
      <div className="flex items-center justify-between p-3 border-b border-zinc-100 bg-zinc-50 rounded-t-lg">
        <div className="flex items-center gap-2">
          <div className="drag-handle cursor-grab active:cursor-grabbing p-1 hover:bg-zinc-200 rounded">
            <GripHorizontal className="w-4 h-4 text-zinc-400" />
          </div>
          <span className="font-medium text-sm flex items-center gap-2">
            <List className="w-4 h-4 text-orange-500" />
            {context.columnName}
          </span>
        </div>
        <button 
          onClick={handleClose}
          className="p-1 hover:bg-zinc-200 rounded"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Current value indicator */}
      {context.currentValue && (
        <div className="px-3 py-2 bg-orange-50 border-b border-zinc-100 flex items-center justify-between">
          <span className="text-sm text-orange-700">
            Actuel : <strong>{context.currentValue}</strong>
          </span>
          <button
            onClick={handleClear}
            className="text-xs px-2 py-0.5 bg-red-100 hover:bg-red-200 text-red-700 rounded"
          >
            Effacer
          </button>
        </div>
      )}
      
      {/* Options list with delete buttons */}
      <div className="max-h-48 overflow-y-auto p-2">
        {options.length === 0 ? (
          <p className="text-sm text-zinc-400 italic p-2 text-center">Aucune option existante</p>
        ) : (
          options.map((option) => (
            <div
              key={option}
              className={`flex items-center justify-between rounded-lg text-sm transition-colors ${
                option === context.currentValue
                  ? 'bg-orange-100 text-orange-800'
                  : 'hover:bg-zinc-100'
              }`}
            >
              <button
                onClick={() => handleSelect(option)}
                className="flex-1 text-left px-3 py-2"
              >
                {option}
                {option === context.currentValue && (
                  <Check className="w-4 h-4 inline ml-2 text-orange-600" />
                )}
              </button>
              <button
                onClick={(e) => handleDeleteOption(option, e)}
                className="p-2 hover:bg-red-100 rounded mr-1 group"
                title="Supprimer cette option"
              >
                <Trash2 className="w-3.5 h-3.5 text-zinc-400 group-hover:text-red-500" />
              </button>
            </div>
          ))
        )}
      </div>
      
      {/* Add custom option */}
      <div className="p-3 border-t border-zinc-100 bg-zinc-50 rounded-b-lg">
        <label className="text-xs text-zinc-500 mb-1 block">Ajouter une nouvelle option :</label>
        <div className="flex gap-2">
          <Input
            type="text"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            placeholder="Nouvelle valeur..."
            className="flex-1 h-8 text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customValue.trim()) {
                handleAddCustom();
              }
            }}
          />
          <Button
            size="sm"
            onClick={handleAddCustom}
            disabled={!customValue.trim()}
            className="h-8 px-3"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
